import {
  Header,
  AppLayout,
  ContentLayout,
  Grid,
  SpaceBetween,
  Button,
  Container,
  DatePicker,
  Input,
  FormField,
  Alert,
  Box,
  Select,
  Spinner,
  DateRangePickerProps,
} from '@cloudscape-design/components';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';

import { ZappyHeader } from '../common/header';
import { VenueFinancialReportTable } from '../components/VenueFinancialReportTable';
import { ErrorMessage } from '../components/ErrorMessage';
import { ViewLayout } from '../components/ViewLayout';
import { getSalesRepReport } from '../common/api/reports';

import { SalesRepFilter } from './filter';
import { SalesRepMetrics } from './metrics';
import { getSalesReps } from '../common/api/sales-reps';
import { SalesRep } from '../common/types';

import { NumberParam, StringParam, useQueryParam, useQueryParams, withDefault } from 'use-query-params';

function Content() {
  const [now, setNow] = useState(() => new Date());

  const defaultDate = useMemo(() => {
    const current = new Date();
    const offset = current.getTimezoneOffset() * 60000;

    return new Date(current.getTime() - offset).toISOString().slice(0, -1).split('T')[0];
  }, []);

  const [{ date: dateParam, days: daysParam, salesRepId }, setSalesFilters] = useQueryParams({
    date: withDefault(StringParam, defaultDate),
    days: withDefault(NumberParam, 30),
    salesRepId: withDefault(StringParam, 'all'),
  });
  const [venuePage, setVenuePageParam] = useQueryParam('venuePage', withDefault(NumberParam, 1));

  const date = dateParam;
  const days = Math.max(daysParam, 1);
  const endDate = useMemo(() => new Date(`${date}T23:59:59`), [date]);

  const repQuery = useQuery({
    queryKey: ['sales-reps'],
    queryFn: () => getSalesReps(),
    staleTime: 3_600_000, // 1 hour
  });

  const [salesRep, setSalesRep] = useState<SalesRep | undefined>(() => {
    if (salesRepId === 'all') {
      return { crm_id: 'all', full_name: 'All' };
    }

    if (salesRepId === 'none') {
      return { crm_id: 'none', full_name: 'None' };
    }

    return { crm_id: salesRepId, full_name: capitalize(salesRepId) };
  });

  useEffect(() => {
    if (salesRep) {
      localStorage.setItem('sales-rep', JSON.stringify(salesRep));
    }
  }, [salesRep]);

  const mapSalesRepId = useCallback(
    (id: string | undefined): SalesRep | undefined => {
      if (!id) {
        return undefined;
      }

      if (id === 'all') {
        return { crm_id: 'all', full_name: 'All' };
      }

      if (id === 'none') {
        return { crm_id: 'none', full_name: 'None' };
      }

      const match = repQuery.data?.find((rep) => rep.crm_id === id);

      if (match) {
        return {
          crm_id: match.crm_id,
          full_name: match.full_name ?? capitalize(match.crm_id),
        };
      }

      return { crm_id: id, full_name: capitalize(id) };
    },
    [repQuery.data],
  );

  useEffect(() => {
    const mapped = mapSalesRepId(salesRepId);

    if (!mapped) {
      if (salesRep) {
        setSalesRep(undefined);
      }

      return;
    }

    if (!salesRep || salesRep.crm_id !== mapped.crm_id || salesRep.full_name !== mapped.full_name) {
      setSalesRep(mapped);
    }
  }, [mapSalesRepId, salesRepId, salesRep]);

  const dataQuery = useQuery({
    queryKey: ['sales-rep', endDate, days, salesRep?.crm_id, now],
    queryFn: () => getSalesRepReport(salesRep?.crm_id ?? '', endDate, days),
    staleTime: 3_600_000, // 1 hour
    enabled: !!salesRep?.crm_id,
  });


  const loading = dataQuery.isPending || dataQuery.isFetching || repQuery.isPending || repQuery.isFetching;
  const error = dataQuery.error || repQuery.error;
  const data = dataQuery.data;

  const handleSetDate = (value: string) => {
    setSalesFilters({ date: value }, 'pushIn');
  };

  const handleSetDays = (value: number) => {
    const normalized = Math.max(value, 1);
    setSalesFilters({ days: normalized }, 'pushIn');
  };

  const handleSetSalesRep = (rep: SalesRep) => {
    setSalesRep(rep);
    setSalesFilters({ salesRepId: rep.crm_id }, 'pushIn');
  };

  const handleSetVenuePage = (page: number) => {
    if (page !== venuePage) {
      setVenuePageParam(page, 'pushIn');
    }
  };

  return (
    <SpaceBetween direction="vertical" size="m">
      <SpaceBetween direction="vertical" size="m">
        <Container>
          <SpaceBetween direction="vertical" size="xs">
            <SalesRepFilter
              date={date}
              days={days}
              loading={loading}
              refresh={() => {
                setNow(new Date());
              }}
              setDate={handleSetDate}
              setDays={handleSetDays}
              salesRep={salesRep}
              setSalesRep={handleSetSalesRep}
              salesReps={repQuery.data?.map((rep) => ({
                crm_id: rep.crm_id,
                full_name: rep.full_name ?? capitalize(rep.crm_id),
              })) ?? []}
            />
            <SalesRepMetrics days={days} error={error} loading={loading} report={data} />
          </SpaceBetween>
        </Container>
        {error ? (
          <ErrorMessage
            error={error}
            loading={loading}
            retry={() => {
              setNow(new Date());
            }}
          />
        ) : undefined}
      </SpaceBetween>
      <VenueFinancialReportTable
        description="Venue financials for sales manager"
        data={data?.report.venue_reports}
        loading={loading}
        error={error}
        hideActions
        hideDateRange
        page={venuePage}
        setPage={handleSetVenuePage}
        variant="container"
      />
    </SpaceBetween>
  );
}

export default function SalesRepsPage() {
  return (
    <ViewLayout
      breadcrumbs={[{ text: 'Sales Manager Reporting', href: '' }]}
      contentType="dashboard"
      header={<ZappyHeader title="Sales Manager Reporting" />}
      headerVariant="high-contrast"
    >
      <Content />
    </ViewLayout>
  );
}

function capitalize(str: string | undefined): string {
  if (!str) {
    return '';
  }

  return str.charAt(0).toUpperCase() + str.slice(1);
}

